import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useActiveSite } from "../context/ActiveSiteContext";
import { useToast } from "../context/ToastContext";
import { isValidWebsiteUrl } from "../utils/urlValidation";
import {
  ChevronDown,
  Plus,
  Globe,
  ShieldAlert,
  FileText,
  LayoutDashboard,
  Search,
  Zap,
  Link as LinkIcon,
  Spider,
  TrendingUp,
  Calendar,
  User,
  Settings,
  LogOut,
  Crown,
  Lock
} from "lucide-react";

const Navbar = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const location = useLocation();
  const {
    activeWorkspace,
    projects,
    activeProject,
    selectProject,
    createProject,
  } = useActiveSite();
  const { showError, showSuccess } = useToast();
  const navigate = useNavigate();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [toolsMenuOpen, setToolsMenuOpen] = useState(false);
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
  const [mobileAccountOpen, setMobileAccountOpen] = useState(false);

  const [newProjectName, setNewProjectName] = useState("");
  const [newProjectUrl, setNewProjectUrl] = useState("");
  const [newProjectSaving, setNewProjectSaving] = useState(false);

  const profileMenuRef = useRef(null);
  const toolsMenuRef = useRef(null);
  const projectDropdownRef = useRef(null);

  const isAdmin = String(user?.role || "").toLowerCase() === "admin";
  const planName = String(user?.plan_name || "free").toLowerCase().trim();
  const isFreePlan = !isAdmin && (!user?.plan_name || planName === "free" || planName === "");
  const canUseSiteCrawl = user?.limits?.can_site_crawl === true || ["solo", "agency", "premium"].includes(planName);
  const maxProjects = Number(user?.limits?.max_projects ?? 3);
  const canCreateProject = projects.length < maxProjects;

  const getNavLinkClass = ({ isActive }) => (isActive ? "active" : undefined);

  const getAdminNavLinkClass = (path, tab) => {
    const currentPath = location.pathname;
    const currentTab = new URLSearchParams(location.search).get("tab") || "users";
    if (currentPath === path && currentTab === tab) {
      return "active";
    }
    return undefined;
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
    closeMenus();
  };

  const closeMenus = () => {
    setMobileMenuOpen(false);
    setProfileMenuOpen(false);
    setToolsMenuOpen(false);
    setProjectDropdownOpen(false);
    setMobileAccountOpen(false);
  };

  const closeCreateProjectModal = () => {
    setShowCreateProjectModal(false);
    setNewProjectName("");
    setNewProjectUrl("");
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    const name = newProjectName.trim();
    const url = newProjectUrl.trim();
    if (!name) {
      showError("Project name is required.");
      return;
    }
    if (!url) {
      showError("Website URL is required.");
      return;
    }
    if (!isValidWebsiteUrl(url)) {
      showError("Please enter a valid website URL.");
      return;
    }
    setNewProjectSaving(true);
    try {
      const proj = await createProject(name, url);
      showSuccess(`Project "${proj.name}" added.`);
      closeCreateProjectModal();
      setProjectDropdownOpen(false);
    } catch (err) {
      showError(err?.response?.data?.error || "Failed to add project.");
    } finally {
      setNewProjectSaving(false);
    }
  };

  useEffect(() => {
    if (mobileMenuOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";
      document.body.style.overflowY = "scroll";
    } else {
      const scrollY = parseFloat(document.body.style.top || "0") * -1;
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.overflowY = "";
      window.scrollTo({ top: scrollY, behavior: "instant" });
    }
    return () => {
      const scrollY = parseFloat(document.body.style.top || "0") * -1;
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.overflowY = "";
      if (scrollY) window.scrollTo({ top: scrollY, behavior: "instant" });
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    const onPointerDown = (event) => {
      if (profileMenuOpen && !profileMenuRef.current?.contains(event.target)) {
        setProfileMenuOpen(false);
      }
      if (toolsMenuOpen && !toolsMenuRef.current?.contains(event.target)) {
        setToolsMenuOpen(false);
      }
      if (projectDropdownOpen && !projectDropdownRef.current?.contains(event.target)) {
        setProjectDropdownOpen(false);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [profileMenuOpen, toolsMenuOpen, projectDropdownOpen]);

  return (
    <nav className={`navbar ${mobileMenuOpen ? "mobile-open" : ""}`}>
      <div className="navbar-header-start">
        <Link to={isAuthenticated ? "/dashboard" : "/"} className="navbar-brand" onClick={closeMenus}>
          <img src="/logo.svg" alt="Nova SEO" className="navbar-logo-img" />
          <span className="navbar-logo">Nova</span>
        </Link>

        {/* Workspace name + project selector */}
        {isAuthenticated && !isAdmin && activeWorkspace && (
          <div className="project-dropdown-container" ref={projectDropdownRef}>
            <button
              type="button"
              className="project-dropdown-trigger"
              onClick={() => setProjectDropdownOpen(!projectDropdownOpen)}
            >
              <Globe size={16} className="project-dropdown-icon" />
              <span className="project-current-url">{activeWorkspace.name}</span>
              <ChevronDown size={14} className="chevron" />
            </button>

            {projectDropdownOpen && (
              <div className="project-dropdown-menu">
                <div className="project-dropdown-header-row">
                  <span className="project-dropdown-header">Projects</span>
                </div>
                <div className="project-dropdown-list">
                  {projects.length === 0 ? (
                    <div className="column-empty">No projects yet.</div>
                  ) : (
                    projects.map((proj) => (
                      <button
                        key={proj.id}
                        type="button"
                        className={`project-dropdown-item ${activeProject?.id === proj.id ? "active" : ""}`}
                        onClick={() => {
                          selectProject(proj);
                          setProjectDropdownOpen(false);
                        }}
                      >
                        <span className="item-label">{proj.name}</span>
                        <span className="item-subtext">{proj.url || "No URL"}</span>
                      </button>
                    ))
                  )}
                </div>
                {canCreateProject && (
                  <button
                    type="button"
                    className="project-dropdown-add-row"
                    onClick={() => {
                      setProjectDropdownOpen(false);
                      setShowCreateProjectModal(true);
                    }}
                  >
                    <Plus size={14} />
                    <span>New project</span>
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className={`navbar-links ${mobileMenuOpen ? "mobile-open" : ""}`}>
        {isAuthenticated ? (
          <>
            {!isAdmin ? (
              <>
                <NavLink to="/dashboard" className={getNavLinkClass} onClick={closeMenus}>Overview</NavLink>
                <NavLink to="/seo-audit" className={getNavLinkClass} onClick={closeMenus}>SEO Audit</NavLink>

                {/* Technical SEO Dropdown */}
                <div className={`nav-dropdown-container ${toolsMenuOpen ? "mobile-open" : ""}`} ref={toolsMenuRef}>
                  <button
                    type="button"
                    className={`nav-dropdown-trigger ${toolsMenuOpen ? "active" : ""}`}
                    onClick={() => setToolsMenuOpen(!toolsMenuOpen)}
                  >
                    Technical SEO <ChevronDown size={14} className="chevron" />
                  </button>

                  {toolsMenuOpen && (
                    <div className="nav-dropdown-menu">
                      <Link to="/pagespeed" className="nav-dropdown-item" onClick={closeMenus}>
                        <Zap size={16} className="nav-dropdown-icon" />
                        <div className="nav-dropdown-content">
                          <div className="nav-dropdown-title">PageSpeed</div>
                          <div className="nav-dropdown-desc">Core Web Vitals and Lighthouse metrics.</div>
                        </div>
                      </Link>
                      <Link to="/broken-links" className="nav-dropdown-item" onClick={closeMenus}>
                        <LinkIcon size={16} className="nav-dropdown-icon" />
                        <div className="nav-dropdown-content">
                          <div className="nav-dropdown-title">Broken Links</div>
                          <div className="nav-dropdown-desc">Scan for 404 dead links and redirections.</div>
                        </div>
                      </Link>
                      <Link to="/deep-audit" className="nav-dropdown-item" onClick={closeMenus}>
                        <FileText size={16} className="nav-dropdown-icon" />
                        <div className="nav-dropdown-content">
                          <div className="nav-dropdown-title">Deep Audit</div>
                          <div className="nav-dropdown-desc">Generate PDF Lighthouse analysis reports.</div>
                        </div>
                      </Link>
                    </div>
                  )}
                </div>

                {canUseSiteCrawl ? (
                  <NavLink to="/site-crawl" className={getNavLinkClass} onClick={closeMenus}>Crawls</NavLink>
                ) : (
                  <Link
                    to="/pricing"
                    onClick={closeMenus}
                    title="Site Crawl is available on Solo & Agency plans"
                    style={{ display: "inline-flex", alignItems: "center", gap: "5px", opacity: 0.55, fontSize: "inherit", color: "inherit", textDecoration: "none", padding: "4px 8px" }}
                  >
                    Crawls <Lock size={11} style={{ flexShrink: 0 }} />
                  </Link>
                )}
                <NavLink to="/keywords" className={getNavLinkClass} onClick={closeMenus} style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                  Keywords
                  <span style={{ fontSize: "0.6rem", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", background: "var(--accent)", color: "#fff", borderRadius: 8, padding: "1px 6px", lineHeight: 1.6, whiteSpace: "nowrap" }}>Soon</span>
                </NavLink>
                <NavLink to="/schedules" className={getNavLinkClass} onClick={closeMenus}>Monitoring</NavLink>
                <NavLink to="/history" className={getNavLinkClass} onClick={closeMenus}>Audits</NavLink>
                <NavLink to="/pricing" className={getNavLinkClass} onClick={closeMenus}>Pricing</NavLink>
              </>
            ) : null}
            {isAdmin && (
              <>
                <NavLink to="/admin/users" className={() => getAdminNavLinkClass("/admin/users", "users")} onClick={closeMenus}>Admin Dashboard</NavLink>
                <NavLink to="/admin/users?tab=projects" className={() => getAdminNavLinkClass("/admin/users", "projects")} onClick={closeMenus}>Projects</NavLink>
                <NavLink to="/admin/users?tab=schedules" className={() => getAdminNavLinkClass("/admin/users", "schedules")} onClick={closeMenus}>Schedules</NavLink>
                <NavLink to="/admin/users?tab=subscribers" className={() => getAdminNavLinkClass("/admin/users", "subscribers")} onClick={closeMenus}>Subscribers</NavLink>
                <NavLink to="/admin/analytics" className={getNavLinkClass} onClick={closeMenus}>Analytics</NavLink>
                <NavLink to="/admin/users?tab=metrics" className={() => getAdminNavLinkClass("/admin/users", "metrics")} onClick={closeMenus}>Metrics</NavLink>
              </>
            )}
            {/* Mobile Account Dropdown */}
            <div className={`navbar-mobile-account nav-dropdown-container ${mobileAccountOpen ? "mobile-open" : ""}`}>
              <button
                type="button"
                className={`nav-dropdown-trigger ${mobileAccountOpen ? "active" : ""}`}
                onClick={() => setMobileAccountOpen(!mobileAccountOpen)}
              >
                Account <ChevronDown size={14} className="chevron" />
                {!isAdmin && isFreePlan && (
                  <span style={{
                    fontSize: "0.6rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                    color: "#fff",
                    borderRadius: 5,
                    padding: "1px 5px",
                    marginLeft: 4,
                  }}>Free</span>
                )}
              </button>
              {mobileAccountOpen && (
                <div className="nav-dropdown-menu">
                  {!isAdmin && isFreePlan && (
                    <Link to="/pricing" className="nav-dropdown-item" onClick={closeMenus} style={{ background: "linear-gradient(135deg, rgba(99,102,241,0.08), rgba(139,92,246,0.08))" }}>
                      <Crown size={16} className="nav-dropdown-icon" style={{ color: "#8b5cf6" }} />
                      <div className="nav-dropdown-content">
                        <div className="nav-dropdown-title" style={{ color: "#6366f1" }}>✨ Upgrade Plan</div>
                        <div className="nav-dropdown-desc">Unlock Crawls, Monitoring &amp; more</div>
                      </div>
                    </Link>
                  )}
                  <Link to="/settings" className="nav-dropdown-item" onClick={closeMenus}>
                    <User size={16} className="nav-dropdown-icon" />
                    <div className="nav-dropdown-content">
                      <div className="nav-dropdown-title">Profile</div>
                    </div>
                  </Link>
                  <Link to="/settings?tab=workspace" className="nav-dropdown-item" onClick={closeMenus}>
                    <Settings size={16} className="nav-dropdown-icon" />
                    <div className="nav-dropdown-content">
                      <div className="nav-dropdown-title">Settings</div>
                    </div>
                  </Link>
                  <button type="button" className="nav-dropdown-item" onClick={handleLogout} style={{ border: 'none', background: 'none', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                    <LogOut size={16} className="nav-dropdown-icon" />
                    <div className="nav-dropdown-content">
                      <div className="nav-dropdown-title">Logout</div>
                    </div>
                  </button>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <NavLink to="/audit" className={getNavLinkClass} onClick={closeMenus}>Quick Audit</NavLink>
            <div className={`nav-dropdown-container ${toolsMenuOpen ? "mobile-open" : ""}`} ref={toolsMenuRef}>
              <button
                type="button"
                className={`nav-dropdown-trigger ${toolsMenuOpen ? "active" : ""}`}
                onClick={() => setToolsMenuOpen(!toolsMenuOpen)}
              >
                Tools <ChevronDown size={14} className="chevron" />
              </button>

              {toolsMenuOpen && (
                <div className="nav-dropdown-menu">
                  <Link to="/pagespeed" className="nav-dropdown-item" onClick={closeMenus}>
                    <Zap size={16} className="nav-dropdown-icon" />
                    <div className="nav-dropdown-content">
                      <div className="nav-dropdown-title">PageSpeed</div>
                      <div className="nav-dropdown-desc">Core Web Vitals and Lighthouse metrics.</div>
                    </div>
                  </Link>
                  <Link to="/broken-links" className="nav-dropdown-item" onClick={closeMenus}>
                    <LinkIcon size={16} className="nav-dropdown-icon" />
                    <div className="nav-dropdown-content">
                      <div className="nav-dropdown-title">Broken Links</div>
                      <div className="nav-dropdown-desc">Scan for 404 dead links and redirections.</div>
                    </div>
                  </Link>
                </div>
              )}
            </div>
            <NavLink to="/pricing" className={getNavLinkClass} onClick={closeMenus}>Pricing</NavLink>
          </>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <div className={`nav-auth ${isAuthenticated ? 'nav-auth-desktop' : ''}`}>
          {isAuthenticated ? (
            <div className="profile-menu" ref={profileMenuRef} style={{ position: "relative", display: "inline-flex", flexDirection: "column", alignItems: "flex-end" }}>
              {isFreePlan && (
                <span style={{
                  position: "absolute",
                  top: "-8px",
                  right: "6px",
                  fontSize: "0.55rem",
                  fontWeight: 800,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                  color: "#fff",
                  borderRadius: 4,
                  padding: "1px 5px",
                  lineHeight: 1.7,
                  whiteSpace: "nowrap",
                  pointerEvents: "none",
                  zIndex: 1,
                }}>Free Plan</span>
              )}
              <button
                type="button"
                className="profile-menu-button"
                onClick={() => setProfileMenuOpen((value) => !value)}
              >
                <User size={16} style={{ marginRight: "6px" }} />
                {user?.name || "Account"}
                <ChevronDown size={14} style={{ marginLeft: "4px" }} />
              </button>
              {profileMenuOpen ? (
                <div className="profile-menu-dropdown">
                  {!isAdmin && isFreePlan && (
                    <>
                      <div style={{
                        padding: "10px 14px",
                        background: "linear-gradient(135deg, rgba(99,102,241,0.08), rgba(139,92,246,0.08))",
                        borderBottom: "1px solid var(--border)",
                        borderRadius: "8px 8px 0 0",
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                          <Crown size={13} style={{ color: "#8b5cf6" }} />
                          <span style={{ fontSize: "0.75rem", fontWeight: 700, color: "#6366f1", textTransform: "uppercase", letterSpacing: "0.04em" }}>Free Plan</span>
                        </div>
                        <p style={{ margin: 0, fontSize: "0.75rem", color: "var(--text-muted)", lineHeight: 1.4 }}>
                          Upgrade to unlock Site Crawl, Monitoring &amp; more.
                        </p>
                        <Link
                          to="/pricing"
                          onClick={closeMenus}
                          style={{
                            display: "block",
                            marginTop: 8,
                            padding: "6px 10px",
                            background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                            color: "#fff",
                            borderRadius: 6,
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            textAlign: "center",
                            textDecoration: "none",
                            letterSpacing: "0.03em",
                          }}
                        >
                          ✨ Upgrade Plan
                        </Link>
                      </div>
                    </>
                  )}
                  <Link to="/settings" onClick={closeMenus}>
                    <User size={14} style={{ marginRight: "8px" }} /> Profile
                  </Link>
                  <Link to="/settings?tab=workspace" onClick={closeMenus}>
                    <Settings size={14} style={{ marginRight: "8px" }} /> Settings
                  </Link>
                  <button type="button" className="profile-menu-logout" onClick={handleLogout}>
                    <LogOut size={14} style={{ marginRight: "8px" }} /> Logout
                  </button>
                </div>
              ) : null}
            </div>
          ) : (
            <Link to="/login?mode=signin" className="nav-login" onClick={closeMenus}>Sign In</Link>
          )}
        </div>

        <button
          className={`hamburger-menu ${mobileMenuOpen ? "active" : ""}`}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle mobile menu"
          aria-expanded={mobileMenuOpen}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>

      {showCreateProjectModal && (
        <div className="modal-overlay" onClick={closeCreateProjectModal} role="presentation">
          <div
            className="modal-card"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="create-project-title"
          >
            <div className="modal-header">
              <h2 id="create-project-title" style={{ margin: 0, fontSize: "1.1rem" }}>Add Project</h2>
              <button type="button" className="modal-close-btn" onClick={closeCreateProjectModal} aria-label="Close">
                ×
              </button>
            </div>
            <p className="modal-lead">Add a website to track SEO performance in your workspace.</p>
            <form onSubmit={handleCreateProject}>
              <div className="field-label" style={{ marginBottom: "14px" }}>
                Project Name
                <input
                  className="input"
                  type="text"
                  placeholder="e.g. Main Website"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  required
                  disabled={newProjectSaving}
                  style={{ marginTop: "6px", width: "100%" }}
                />
              </div>
              <div className="field-label" style={{ marginBottom: "20px" }}>
                Website URL
                <input
                  className="input"
                  type="text"
                  placeholder="e.g. example.com"
                  value={newProjectUrl}
                  onChange={(e) => setNewProjectUrl(e.target.value)}
                  required
                  disabled={newProjectSaving}
                  style={{ marginTop: "6px", width: "100%" }}
                />
              </div>
              <button type="submit" className="btn btn-primary" disabled={newProjectSaving || !newProjectName.trim() || !newProjectUrl.trim()} style={{ width: "100%" }}>
                {newProjectSaving ? "Creating..." : "Create Project"}
              </button>
            </form>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
